import { useCallback, useEffect, useRef } from 'react';
import { telemetryService, SeverityLevel } from '@/services/telemetryService';
import { useLocation } from 'react-router-dom';

/**
 * React integration for the telemetry service.
 * Provides hooks for tracking events, page views, exceptions, and timing.
 */

type TelemetryApi = {
  trackEvent: (name: string, properties?: Record<string, unknown>, measurements?: Record<string, number>) => void;
  trackException: (error: Error | unknown, properties?: Record<string, unknown>) => void;
  trackMetric: (name: string, average: number, properties?: Record<string, unknown>) => void;
  trackTrace: (message: string, severityLevel?: SeverityLevel, properties?: Record<string, unknown>) => void;
};

/** Module-level stable reference — avoids re-creating functions on every render. */
const telemetryApi: TelemetryApi = {
  trackEvent(name, properties, measurements) {
    telemetryService.trackEvent(name, properties, measurements);
  },
  trackException(error, properties) {
    telemetryService.trackException(error, properties);
  },
  trackMetric(name, average, properties) {
    telemetryService.trackMetric(name, average, properties);
  },
  trackTrace(message, severityLevel, properties) {
    telemetryService.trackTrace(message, severityLevel, properties);
  },
};

/**
 * Core telemetry hook — returns tracking functions.
 * Safe to call even when telemetry is disabled (service methods are no-ops).
 * Returns a stable reference that never changes between renders.
 */
export function useTelemetry(): TelemetryApi {
  return telemetryApi;
}

/**
 * Tracks page view on mount and duration on unmount.
 * Tracks when the pathname changes; query string changes on the same
 * path are treated as filter updates, not new page views.
 */
export function useTrackPageView(pageName: string, properties?: Record<string, unknown>): void {
  const location = useLocation();
  const mountTimeRef = useRef<number>(Date.now());

  useEffect(() => {
    mountTimeRef.current = Date.now();
    telemetryService.trackPageView(pageName, location.pathname, properties);

    return () => {
      const duration = Date.now() - mountTimeRef.current;
      telemetryService.trackMetric(`page.duration.${pageName}`, duration, properties);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageName, location.pathname]);
}

/**
 * Returns start/stop functions for measuring operation duration.
 * Automatically reports as a metric when stop() is called.
 */
export function useTrackTiming(metricName: string) {
  const startTimeRef = useRef<number | null>(null);

  const start = useCallback(() => {
    startTimeRef.current = performance.now();
  }, []);

  const stop = useCallback(
    (properties?: Record<string, unknown>) => {
      if (startTimeRef.current === null) return;
      const duration = performance.now() - startTimeRef.current;
      telemetryService.trackMetric(metricName, duration, {
        ...properties,
        unit: 'ms',
      });
      startTimeRef.current = null;
      return duration;
    },
    [metricName]
  );

  return { start, stop };
}

const PAGE_NAMES: Record<string, string> = {
  '/': 'Home',
  '/projects': 'Projects',
  '/login': 'Login',
};

function getPageNameFromPath(path: string): string {
  const exactMatch = PAGE_NAMES[path];
  if (exactMatch) return exactMatch;

  if (path.match(/^\/workflows\/[^/]+\/runs\//)) return 'WorkflowRun';
  if (path.match(/^\/workflows\/[^/]+$/)) return 'WorkflowDesigner';

  return 'Unknown';
}

/**
 * Auto-tracks route changes as page views.
 * Place once in the app (inside BrowserRouter).
 */
export function useRouteTracking(): void {
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    if (location.pathname !== prevPathRef.current) {
      const pageName = getPageNameFromPath(location.pathname);
      telemetryService.trackPageView(pageName, location.pathname + location.search, {
        referrer: prevPathRef.current,
      });
      prevPathRef.current = location.pathname;
    }
  }, [location.pathname, location.search]);
}

export { SeverityLevel };
